import { useEffect } from 'react';
import { useUIStore } from '@/state/store';
import { Header } from '@/components/Header';
import { Sidebar } from '@/components/Sidebar';
import { Rail } from '@/components/Rail';
import { ChatDrawer } from '@/components/ChatDrawer';
import { Dashboard } from '@/views/Dashboard';
import { LinksView } from '@/views/LinksView';
import { WrapupView } from '@/views/WrapupView';
import { getPendingDocCountSync, refreshPendingDocCount } from '@/lib/localDocs';
import { Incoming } from '@/views/Incoming';
import { InCall } from '@/views/InCall';
import { Login } from '@/views/Login';
import { CmdK } from '@/components/CmdK';
import { HelpPanel } from '@/components/HelpPanel';
import { OnboardingModal } from '@/components/OnboardingModal';
import { SettingsDrawer } from '@/components/SettingsDrawer';
import { EscalateDrawer, TransferDrawer } from '@/components/CallDrawers';
import { JoinRequestPrompt } from '@/components/JoinRequestPrompt';
import { TransferOfferPrompt } from '@/components/TransferOfferPrompt';
import { SessionConflictPrompt } from '@/components/SessionConflictPrompt';
import { PwaToast } from '@/components/PwaToast';
import { PreNotification } from '@/components/PreNotification';
import { WrapBanner } from '@/components/WrapBanner';
import { SessionExpiryWarning } from '@/components/SessionExpiryWarning';
import { FolderAccessBanner } from '@/components/FolderAccessBanner';
import { useRingtone } from '@/hooks/useAudio';
import { useReachability, useWakeLock, useAudioHeartbeat } from '@/hooks/useReachability';
import { useGlobalShortcuts } from '@/hooks/useShortcuts';
import { usePushSubscription } from '@/hooks/usePushSubscription';
import { useAgentSocket } from '@/hooks/useAgentSocket';
import { useNotificationActions } from '@/hooks/useNotificationActions';
import { useSessionLifecycle } from '@/hooks/useSessionLifecycle';
import { StateBanner } from '@/components/StateBanner';
import { VersionPill } from '@/components/VersionPill';
import { isTokenValid } from '@/lib/auth';
import { loadCallSession, f5RestoreEnabled } from '@/lib/callSession';

export default function App() {
  const view = useUIStore((s) => s.view);
  // #1: Die Ansichts-Umschaltleiste war ein Mockup-Helfer — nur noch via ?statebanner=1 (Dev).
  const showStateBanner = new URLSearchParams(window.location.search).get('statebanner') === '1';
  const status = useUIStore((s) => s.status);
  const setView = useUIStore((s) => s.setView);

  // Welle 6f: Session-Lifecycle (Token-Ablauf-Warnung + Auto-Logout)
  const session = useSessionLifecycle();

  // pre87d: Vor Browserschließen/Reload warnen, wenn noch nicht abgelegte (lokale)
  // Session-Dokumente vorliegen — sonst gehen sie verloren.
  useEffect(() => {
    void refreshPendingDocCount();
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (getPendingDocCountSync() > 0) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
      return undefined;
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  // Auto-detect login: wenn Token gültig, springe direkt ins Dashboard
  useEffect(() => {
    if (!isTokenValid()) {
      if (view !== 'login') setView('login');
      return;
    }
    // Welle 8c-pre47 (F5-Restore): War beim Reload ein Call aktiv, nehmen wir ihn wieder
    // auf — rtcToken/activeCallId + Anzeigedaten rehydrieren und direkt in die In-Call-
    // Ansicht springen. Der useWebRtc-Hook verbindet dann neu; die Stele baut auf ihr
    // transientes bye hin eine frische PeerConnection auf (rebuildAvPeer).
    if (f5RestoreEnabled()) {
      const sess = loadCallSession();
      if (sess) {
        (useUIStore.getState() as any).rtcToken = sess.rtcToken;
        (useUIStore.getState() as any).activeCallId = sess.callId;
        (window as any).__rtcToken = sess.rtcToken;
        (window as any).__activeCallId = sess.callId;
        if (sess.incoming) (window as any).__incomingCall = sess.incoming;
        (window as any).__f5Restoring = true;
        console.log('[f5] aktiver Call erkannt → Wiederaufnahme, callId=', sess.callId);
        setView('incall');
        return;
      }
    }
    if (view === 'login') setView('dashboard');
  }, []);

  // Background-Mechanik
  useReachability();
  useWakeLock(status === 'available' || view.startsWith('incall'));
  useAudioHeartbeat(status === 'available');
  useRingtone();
  useGlobalShortcuts();

  // Echte Backend-Verbindung (nur wenn eingeloggt)
  const isLoggedIn = view !== 'login';
  usePushSubscription();
  useAgentSocket();
  useNotificationActions();

  if (view === 'login') {
    return (
      <>
        {showStateBanner && <StateBanner />}
        <div className="app minimal">
          <Login />
        </div>
        <VersionPill />
      </>
    );
  }

  return (
    <>
      {showStateBanner && <StateBanner />}
      <div className="app">
        <Header />
        <Sidebar />
        <main className="main" id="main-stage">
          {view === 'dashboard' && <Dashboard />}
          {view === 'links' && <LinksView />}
          {view === 'wrapup' && <WrapupView />}
          {view === 'incoming' && <Incoming />}
          {view.startsWith('incall') && <InCall />}
          {view === 'wrap' && <Dashboard />}
        </main>
        <Rail />
      </div>
      <ChatDrawer />

      <PreNotification />
      <EscalateDrawer />
      <TransferDrawer />
      <JoinRequestPrompt />
      <TransferOfferPrompt />
      <SessionConflictPrompt />
      <CmdK />
      <HelpPanel />
      <OnboardingModal />
      <SettingsDrawer />
      <PwaToast />
      <WrapBanner />
      <VersionPill />
      <FolderAccessBanner />
      {session.showExpiryWarning && <SessionExpiryWarning onDismiss={session.dismissWarning} />}
    </>
  );
}
